import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminEmailDialog from "@/components/AdminEmailDialog";
import { toast } from "sonner";
import { Plus, Search, RefreshCw, Download, Upload, X, Phone, Mail, Globe, Save, Trash2,
         Store, MapPin, Tag, MessageCircle, AlertTriangle } from "lucide-react";

const CRM = [
  { value: "prospect",      label: "Prospect",      cls: "bg-slate-100 text-slate-700 border border-slate-200" },
  { value: "contacte",      label: "Contacté",      cls: "bg-blue-100 text-blue-800 border border-blue-200" },
  { value: "en_discussion", label: "En discussion", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  { value: "partenaire",    label: "Partenaire",    cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
  { value: "suspendu",      label: "Suspendu",      cls: "bg-orange-100 text-orange-800 border border-orange-200" },
  { value: "refuse",        label: "Refusé",        cls: "bg-rose-100 text-rose-800 border border-rose-200" },
];
const labelOf = (v) => CRM.find((x) => x.value === v)?.label || (v || "—");
const clsOf = (v) => CRM.find((x) => x.value === v)?.cls || "bg-slate-100 text-slate-600";
const whatsappHref = (p) => {
  const d = (p || "").replace(/[^\d]/g, "");
  if (!d) return "";
  return `https://wa.me/${d.startsWith("0") ? "33" + d.slice(1) : d}`;
};

function downloadCsv(url, filename) {
  api.get(url, { responseType: "blob" }).then((r) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  });
}

export default function AdminPros() {
  const [biz, setBiz] = useState([]);
  const [q, setQ] = useState("");
  const [crmFilter, setCrmFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [devisFilter, setDevisFilter] = useState("");
  const [cats, setCats] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [elig, setElig] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get("/admin/businesses").then((r) => { setBiz(r.data); setSel(new Set()); });
  const loadElig = () => api.get("/devis/admin/pros-eligibility").then((r) => setElig(r.data.eligibility || {})).catch(() => {});
  useEffect(() => { load(); loadElig(); api.get("/admin/categories").then((r)=>setCats(r.data)); }, []);

  const toggleDevis = async (id, current) => {
    setElig((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), devis_enabled: !current } }));
    try {
      await api.post(`/devis/admin/pros/${id}/eligibility`, { enabled: !current });
      toast.success(!current ? "Partenaire activé pour les devis" : "Partenaire exclu des devis");
    } catch (e) {
      setElig((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), devis_enabled: current } }));
      toast.error(e?.response?.data?.detail || "Action impossible");
    }
  };
  useEffect(() => {
    api.post("/admin/pros/mark-seen")
      .then(() => window.dispatchEvent(new CustomEvent("admin:todos-refresh")))
      .catch(() => {});
  }, []);
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setSelectedId(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const cities = useMemo(() => [...new Set(biz.map((b) => b.city).filter(Boolean))].sort(), [biz]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    return biz.filter((b) => {
      if (crmFilter && (b.crm_status || (b.status==="active"?"partenaire":"")) !== crmFilter) return false;
      if (cityFilter && b.city !== cityFilter) return false;
      if (devisFilter === "on" && !elig[b.id]?.devis_enabled) return false;
      if (devisFilter === "off" && elig[b.id]?.devis_enabled) return false;
      if (!s) return true;
      return [b.name, b.legal_name, b.owner?.email, b.owner?.first_name, b.owner?.last_name,
              b.phone, b.city, b.address, b.website]
        .some((v) => v && String(v).toLowerCase().includes(s));
    });
  }, [biz, q, crmFilter, cityFilter, devisFilter, elig]);

  const counts = useMemo(() => {
    const c = { prospect: 0, contacte: 0, en_discussion: 0, partenaire: 0, suspendu: 0, refuse: 0 };
    biz.forEach((b) => {
      const key = b.crm_status || (b.status==="active"?"partenaire":"prospect");
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [biz]);

  const toggleSel = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = filtered.length > 0 && filtered.every((b) => sel.has(b.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(filtered.map((b) => b.id)));
  const bulkCrm = async (crm_status) => {
    try {
      const { data } = await api.post("/admin/businesses/bulk", { ids: [...sel], action: "crm", crm_status });
      toast.success(`${data.updated ?? sel.size} partenaire(s) → ${labelOf(crm_status)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };
  const bulkDelete = async () => {
    if (!confirm(`⚠️ Supprimer DÉFINITIVEMENT ${sel.size} partenaire(s) et toutes leurs offres ? Action irréversible.`)) return;
    try {
      const { data } = await api.post("/admin/businesses/bulk", { ids: [...sel], action: "delete" });
      toast.success(`${data.deleted ?? sel.size} partenaire(s) supprimé(s) · ${data.offers_deleted || 0} offre(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const [emailOpen, setEmailOpen] = useState(false);
  const [emSubject, setEmSubject] = useState("");
  const [emMsg, setEmMsg] = useState("");
  const [emSending, setEmSending] = useState(false);
  const sendBulkEmail = async () => {
    if (emSubject.trim().length < 2 || emMsg.trim().length < 5) { toast.error("Sujet et message requis."); return; }
    setEmSending(true);
    try {
      const { data } = await api.post("/admin/businesses/bulk-email", { ids: [...sel], subject: emSubject.trim(), message: emMsg.trim() });
      toast.success(`Email envoyé à ${data.sent} partenaire(s)${data.skipped ? ` · ${data.skipped} sans email ignoré(s)` : ""}`);
      setEmailOpen(false); setEmSubject(""); setEmMsg("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setEmSending(false); }
  };
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const runImport = async () => {
    const rows = importText.split("\n").map((l) => l.trim()).filter(Boolean)
      .filter((l) => !/^nom\s*[;,\t]/i.test(l))
      .map((l) => {
        const p = l.split(/[;,\t]/).map((x) => x.trim());
        return { name: p[0] || "", email: p[1] || "", phone: p[2] || "", city: p[3] || "", category: p[4] || "" };
      }).filter((r) => r.name);
    if (rows.length === 0) { toast.error("Aucune ligne valide. Format : Nom, Email, Téléphone, Ville, Catégorie"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/crm/import", { rows });
      toast.success(`${data.created} prospect(s) importé(s)${data.skipped ? ` · ${data.skipped} doublon(s) ignoré(s)` : ""}`);
      setImportOpen(false); setImportText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import impossible"); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="admin-partners-title">Partenaires / Commerçants</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span className="text-slate-700 font-semibold">{counts.prospect} prospect(s)</span>
            <span className="text-blue-700 font-semibold">{counts.contacte} contacté(s)</span>
            <span className="text-amber-700 font-semibold">{counts.en_discussion} en discussion</span>
            <span className="text-emerald-700 font-semibold">{counts.partenaire} partenaire(s)</span>
            {counts.suspendu ? <span className="text-orange-700 font-semibold">{counts.suspendu} suspendu(s)</span> : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input placeholder="Nom, email, tél, ville…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 w-full sm:w-64" data-testid="partners-search"/>
          </div>
          <Select value={crmFilter || "all"} onValueChange={(v)=>setCrmFilter(v==="all"?"":v)}>
            <SelectTrigger className="w-40" data-testid="partners-crm-filter"><SelectValue placeholder="Statut CRM"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {CRM.map((s)=>(<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={cityFilter || "all"} onValueChange={(v)=>setCityFilter(v==="all"?"":v)}>
            <SelectTrigger className="w-36" data-testid="partners-city-filter"><SelectValue placeholder="Ville"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes villes</SelectItem>
              {cities.map((c)=>(<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={devisFilter || "all"} onValueChange={(v)=>setDevisFilter(v==="all"?"":v)}>
            <SelectTrigger className="w-40" data-testid="partners-devis-filter"><SelectValue placeholder="Devis"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous (devis)</SelectItem>
              <SelectItem value="on">Reçoit les devis</SelectItem>
              <SelectItem value="off">Exclus des devis</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={()=>downloadCsv("/admin/businesses-export", "partenaires.csv")} data-testid="partners-export-btn">
            <Download size={14} className="mr-1"/> Export
          </Button>
          <Button variant="outline" onClick={()=>setImportOpen(true)} data-testid="partners-import-btn">
            <Upload size={14} className="mr-1"/> Importer
          </Button>
          <Button variant="outline" onClick={load} data-testid="partners-refresh"><RefreshCw size={14}/></Button>
          <Link to="/admin/professionnels/nouveau">
            <Button className="btn-shine text-white" data-testid="admin-new-pro-btn"><Plus size={16} className="mr-1"/> Nouveau</Button>
          </Link>
        </div>
      </div>

      {/* Desktop */}
      <div className="mt-6 hidden md:block bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        {sel.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-slate-900 text-white px-4 py-2.5" data-testid="partners-bulk-bar">
            <span className="text-sm font-semibold">{sel.size} sélectionné(s)</span>
            <div className="flex-1" />
            <button onClick={()=>bulkCrm("partenaire")} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold" data-testid="partners-bulk-approve">Marquer partenaire</button>
            <button onClick={()=>bulkCrm("contacte")} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-xs font-semibold" data-testid="partners-bulk-contacted">Contacté</button>
            <button onClick={()=>bulkCrm("suspendu")} className="px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-xs font-semibold" data-testid="partners-bulk-suspend">Suspendre</button>
            <button onClick={()=>bulkCrm("refuse")} className="px-3 py-1.5 rounded-lg bg-slate-600 hover:bg-slate-500 text-xs font-semibold" data-testid="partners-bulk-refuse">Refuser</button>
            <button onClick={()=>setEmailOpen(true)} className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-xs font-semibold inline-flex items-center gap-1" data-testid="partners-bulk-email"><Mail size={14}/> Email</button>
            <button onClick={bulkDelete} className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-xs font-semibold inline-flex items-center gap-1" data-testid="partners-bulk-delete"><Trash2 size={14}/> Supprimer</button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="p-3 w-10 text-center"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Tout sélectionner" data-testid="partners-select-all"/></th>
              <th className="p-3 text-left">Entreprise</th>
              <th className="p-3 text-left">Contact</th>
              <th className="p-3 text-left">Ville</th>
              <th className="p-3 text-left">Statut CRM</th>
              <th className="p-3 text-left">Offres</th>
              <th className="p-3 text-center" title="Reçoit les demandes de devis">Devis</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => {
              const crm = b.crm_status || (b.status==="active"?"partenaire":"prospect");
              return (
                <tr key={b.id} className={`border-t border-slate-100 hover:bg-slate-50 ${sel.has(b.id)?"bg-amber-50":""}`} data-testid={`partner-row-${b.id}`}>
                  <td className="p-3 text-center"><input type="checkbox" checked={sel.has(b.id)} onChange={()=>toggleSel(b.id)} aria-label="Sélectionner" data-testid={`partner-select-${b.id}`}/></td>
                  <td className="p-3 cursor-pointer" onClick={()=>setSelectedId(b.id)}>
                    <div className="font-semibold text-slate-900">{b.name}</div>
                    {b.legal_name && <div className="text-xs text-slate-500">{b.legal_name}</div>}
                  </td>
                  <td className="p-3 cursor-pointer" onClick={()=>setSelectedId(b.id)}>
                    <div className="text-sm">{b.contact_first_name || b.owner?.first_name} {b.contact_last_name || b.owner?.last_name}</div>
                    <div className="text-xs text-slate-500">{b.owner?.email}</div>
                    {b.phone && <div className="text-xs text-slate-500">{b.phone}</div>}
                  </td>
                  <td className="p-3 cursor-pointer" onClick={()=>setSelectedId(b.id)}>{b.city || "—"}</td>
                  <td className="p-3 cursor-pointer" onClick={()=>setSelectedId(b.id)}><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${clsOf(crm)}`}>{labelOf(crm)}</span></td>
                  <td className="p-3 font-mono cursor-pointer" onClick={()=>setSelectedId(b.id)}>{b.offer_count || 0}</td>
                  <td className="p-3 text-center" onClick={(e)=>e.stopPropagation()}>
                    <input type="checkbox" className="w-4 h-4 accent-[#E05638] cursor-pointer"
                      checked={!!elig[b.id]?.devis_enabled}
                      onChange={()=>toggleDevis(b.id, !!elig[b.id]?.devis_enabled)}
                      title={elig[b.id]?.has_trades ? "Reçoit les demandes de devis correspondant à son métier" : "Catégorie hors devis — cochez pour forcer la réception (si un métier est renseigné côté pro)"}
                      data-testid={`partner-devis-${b.id}`} />
                  </td>
                  <td className="p-3 text-right" onClick={(e)=>e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/admin/offres/nouvelle?business=${b.id}`}>
                        <Button size="sm" variant="outline" data-testid={`partner-add-offer-${b.id}`}><Plus size={12} className="mr-1"/> Offre</Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-500">Aucun partenaire.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="mt-4 md:hidden space-y-2">
        {sel.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-slate-900 text-white px-3 py-2 rounded-xl" data-testid="partners-bulk-bar-mobile">
            <span className="text-xs font-semibold">{sel.size} sél.</span>
            <div className="flex-1" />
            <button onClick={()=>bulkCrm("partenaire")} className="px-2.5 py-1 rounded-lg bg-emerald-600 text-xs font-semibold">Partenaire</button>
            <button onClick={()=>bulkCrm("suspendu")} className="px-2.5 py-1 rounded-lg bg-orange-600 text-xs font-semibold">Suspendre</button>
            <button onClick={()=>setEmailOpen(true)} className="px-2.5 py-1 rounded-lg bg-indigo-600 text-xs font-semibold">Email</button>
            <button onClick={bulkDelete} className="px-2.5 py-1 rounded-lg bg-rose-600 text-xs font-semibold">Supprimer</button>
          </div>
        )}
        {filtered.map((b) => {
          const crm = b.crm_status || (b.status==="active"?"partenaire":"prospect");
          return (
            <div key={b.id} data-testid={`partner-card-${b.id}`}
                 className={`w-full bg-white border rounded-xl p-3 flex items-start gap-2 ${sel.has(b.id)?"border-amber-400 bg-amber-50":"border-slate-200"}`}>
              <input type="checkbox" checked={sel.has(b.id)} onChange={()=>toggleSel(b.id)} className="mt-1" aria-label="Sélectionner" data-testid={`partner-select-m-${b.id}`}/>
              <button onClick={()=>setSelectedId(b.id)} className="flex-1 text-left min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 truncate">{b.owner?.email} · {b.city || "—"}</div>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${clsOf(crm)}`}>{labelOf(crm)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-xs text-slate-500">{b.offer_count || 0} offre(s)</span>
                  <label className="inline-flex items-center gap-1.5 text-xs text-slate-600" onClick={(e)=>e.stopPropagation()}>
                    <input type="checkbox" className="w-4 h-4 accent-[#E05638]" checked={!!elig[b.id]?.devis_enabled}
                      onChange={()=>toggleDevis(b.id, !!elig[b.id]?.devis_enabled)} data-testid={`partner-devis-m-${b.id}`} /> Reçoit les devis
                  </label>
                </div>
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-center text-slate-500 p-6">Aucun partenaire.</div>}
      </div>

      {selectedId && <PartnerDrawer id={selectedId} onClose={()=>setSelectedId(null)} onChanged={load} cats={cats} cities={cities}/>}

      {importOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={()=>setImportOpen(false)}>
          <div className="bg-white w-full max-w-lg rounded-2xl p-5" onClick={(e)=>e.stopPropagation()} data-testid="partners-import-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold">Importer des prospects</h3>
              <button onClick={()=>setImportOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18}/></button>
            </div>
            <p className="text-xs text-slate-500 mb-2">Collez une ligne par commerce, au format : <b>Nom, Email, Téléphone, Ville, Catégorie</b> (séparateur virgule, point-virgule ou tabulation). Les doublons sont ignorés.</p>
            <Textarea rows={8} value={importText} onChange={(e)=>setImportText(e.target.value)} placeholder={"Boulangerie Durand, contact@durand.fr, 0237000000, Chartres, Boulangeries\nGarage Léon, , 0237111111, Lucé, Garages automobiles"} data-testid="partners-import-text"/>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={()=>setImportOpen(false)}>Annuler</Button>
              <Button disabled={importing} onClick={runImport} className="btn-shine text-white" data-testid="partners-import-run">{importing?"Import…":"Importer"}</Button>
            </div>
          </div>
        </div>
      )}

      {emailOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={()=>setEmailOpen(false)}>
          <div className="bg-white w-full max-w-lg rounded-2xl p-5" onClick={(e)=>e.stopPropagation()} data-testid="partners-email-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold">Email à {sel.size} partenaire(s)</h3>
              <button onClick={()=>setEmailOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18}/></button>
            </div>
            <Label>Sujet</Label>
            <Input value={emSubject} onChange={(e)=>setEmSubject(e.target.value)} placeholder="Ex : Nouvelle opportunité PROXIreduc-28" data-testid="partners-email-subject"/>
            <Label className="mt-3 block">Message</Label>
            <Textarea rows={6} value={emMsg} onChange={(e)=>setEmMsg(e.target.value)} placeholder="Votre message aux partenaires…" data-testid="partners-email-message"/>
            <div className="mt-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2">
              Les partenaires sans adresse email seront ignorés. Envoi via le service email PROXIreduc-28.
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={()=>setEmailOpen(false)}>Annuler</Button>
              <Button disabled={emSending} onClick={sendBulkEmail} className="btn-shine text-white" data-testid="partners-email-send">{emSending?"Envoi…":"Envoyer"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PartnerDrawer({ id, onClose, onChanged, cats, cities }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("info");

  useEffect(() => {
    api.get(`/admin/businesses/${id}`).then((r) => {
      setData(r.data);
      setForm({ ...r.data.business,
        crm_status: r.data.business.crm_status || (r.data.business.status === "active" ? "partenaire" : "prospect"),
        contact_first_name: r.data.business.contact_first_name || r.data.owner?.first_name || "",
        contact_last_name: r.data.business.contact_last_name || r.data.owner?.last_name || "",
      });
    });
  }, [id]);

  if (!data || !form) return null;
  const b = data.business;
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const wa = whatsappHref(b.phone);

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name, description: form.description || "", category_id: form.category_id,
        address: form.address || "", postal_code: form.postal_code || "", city: form.city || "",
        phone: form.phone || "", email: form.email || "", website: form.website || "",
        opening_hours: form.opening_hours || "", coverage: form.coverage || "local",
        legal_name: form.legal_name || "",
        contact_first_name: form.contact_first_name || "",
        contact_last_name: form.contact_last_name || "",
        social_facebook: form.social_facebook || "", social_instagram: form.social_instagram || "",
        social_linkedin: form.social_linkedin || "",
        crm_status: form.crm_status,
        internal_notes: form.internal_notes || "",
        first_contact_at: form.first_contact_at || null,
      };
      await api.patch(`/admin/businesses/${id}`, payload);
      toast.success("Partenaire enregistré");
      onChanged();
      const r = await api.get(`/admin/businesses/${id}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const remove = async () => {
    const label = `${b.name}`;
    const nOffers = data.offers.length;
    const confirmMsg = `⚠️ Supprimer DÉFINITIVEMENT « ${label} » ?\nCela supprimera aussi ${nOffers} offre(s) associée(s).\nCette action est irréversible. Tapez "SUPPRIMER" pour confirmer.`;
    const answer = prompt(confirmMsg);
    if (answer !== "SUPPRIMER") return;
    await api.delete(`/admin/businesses/${id}`);
    toast.success("Partenaire supprimé");
    onChanged(); onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex md:justify-end p-0 md:p-4" onClick={onClose}>
      <div className="bg-white w-full md:max-w-2xl md:rounded-2xl overflow-y-auto md:my-auto md:max-h-[92vh] shadow-2xl"
           onClick={(e)=>e.stopPropagation()} data-testid="partner-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#E05638] font-bold">Partenaire</div>
            <div className="font-heading text-xl font-bold truncate">{b.name}</div>
            <div className="text-xs text-slate-500">{b.legal_name || "—"} · Créé le {new Date(b.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="partner-close"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-4">
          {/* CTA rapides */}
          <div className="grid grid-cols-4 gap-2">
            <a href={b.phone ? `tel:${b.phone}` : undefined}
               className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold ${b.phone ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-100 text-slate-400 pointer-events-none"}`}
               data-testid="partner-call"><Phone size={12}/> Appeler</a>
            <AdminEmailDialog recipients={[b.email || data.owner?.email]} defaultSubject="À propos de votre fiche PROXIreduc-28" testId="partner-mail" />
            <a href={wa || undefined} target="_blank" rel="noreferrer"
               className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold ${wa ? "bg-[#25D366] text-white" : "bg-slate-100 text-slate-400 pointer-events-none"}`}
               data-testid="partner-wa"><MessageCircle size={12}/> WhatsApp</a>
            <a href={b.website || undefined} target="_blank" rel="noreferrer"
               className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold ${b.website ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400 pointer-events-none"}`}
               data-testid="partner-web"><Globe size={12}/> Site</a>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-slate-200 -mx-4 px-4 overflow-x-auto">
            {[["info","Infos"],["offers",`Offres (${data.offers.length})`],["requests",`Demandes (${data.requests.length})`],["notes","Notes / CRM"],["history",`Historique (${data.activity.length})`]].map(([k,l]) => (
              <button key={k} onClick={()=>setTab(k)} data-testid={`partner-tab-${k}`}
                      className={`px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 ${tab===k?"border-[#E05638] text-slate-900":"border-transparent text-slate-500"}`}>{l}</button>
            ))}
          </div>

          {tab==="info" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nom commercial</Label><Input value={form.name} onChange={set("name")}/></div>
              <div><Label>Raison sociale</Label><Input value={form.legal_name} onChange={set("legal_name")}/></div>
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category_id} onValueChange={(v)=>setForm((s)=>({...s, category_id: v}))}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{cats.map((c)=>(<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Contact prénom</Label><Input value={form.contact_first_name} onChange={set("contact_first_name")}/></div>
              <div><Label>Contact nom</Label><Input value={form.contact_last_name} onChange={set("contact_last_name")}/></div>
              <div><Label>Email</Label><Input value={form.email} onChange={set("email")}/></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")}/></div>
              <div className="col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={set("address")}/></div>
              <div><Label>Code postal</Label><Input value={form.postal_code} onChange={set("postal_code")}/></div>
              <div>
                <Label>Ville</Label>
                <Select value={form.city} onValueChange={(v)=>setForm((s)=>({...s, city: v}))}>
                  <SelectTrigger><SelectValue placeholder="Choisir"/></SelectTrigger>
                  <SelectContent>{cities.map((c)=>(<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Site web</Label><Input value={form.website} onChange={set("website")}/></div>
              <div><Label>Facebook</Label><Input value={form.social_facebook || ""} onChange={set("social_facebook")}/></div>
              <div><Label>Instagram</Label><Input value={form.social_instagram || ""} onChange={set("social_instagram")}/></div>
              <div><Label>LinkedIn</Label><Input value={form.social_linkedin || ""} onChange={set("social_linkedin")}/></div>
              <div><Label>Horaires</Label><Input value={form.opening_hours} onChange={set("opening_hours")}/></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description || ""} onChange={set("description")}/></div>
            </div>
          )}

          {tab==="offers" && (
            <div className="space-y-2">
              {data.offers.length === 0 && <div className="text-sm text-slate-500 text-center p-6">Aucune offre.</div>}
              {data.offers.map((o) => (
                <Link key={o.id} to={`/admin/offres/${o.id}/editer`}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 border border-slate-200">
                  <Tag size={16} className="text-slate-400"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{o.title}</div>
                    <div className="text-xs text-slate-500">{o.discount} · {o.city}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    o.status==="published"?"bg-emerald-100 text-emerald-700":
                    o.status==="pending"?"bg-amber-100 text-amber-700":
                    o.status==="expired"?"bg-slate-200 text-slate-600":
                    o.status==="rejected"?"bg-rose-100 text-rose-700":"bg-slate-100"
                  }`}>{o.status}</span>
                </Link>
              ))}
              <Link to={`/admin/offres/nouvelle?business=${b.id}`}>
                <Button variant="outline" className="w-full" data-testid="partner-add-offer-drawer"><Plus size={14} className="mr-1"/> Nouvelle offre pour ce partenaire</Button>
              </Link>
            </div>
          )}

          {tab==="requests" && (
            <div className="space-y-2">
              {data.requests.length === 0 && <div className="text-sm text-slate-500 text-center p-6">Aucune demande liée.</div>}
              {data.requests.map((r) => (
                <div key={r.id} className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-sm">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.email} · {new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
                    </div>
                    <span className="text-xs font-semibold text-slate-600">{r.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-600 line-clamp-2">{r.message}</div>
                </div>
              ))}
            </div>
          )}

          {tab==="notes" && (
            <div className="space-y-3">
              <div>
                <Label>Statut CRM</Label>
                <Select value={form.crm_status} onValueChange={(v)=>setForm((s)=>({...s, crm_status: v}))}>
                  <SelectTrigger data-testid="partner-crm-select"><SelectValue/></SelectTrigger>
                  <SelectContent>{CRM.map((s)=>(<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date de prise de contact</Label>
                <Input type="date"
                       value={form.first_contact_at ? String(form.first_contact_at).slice(0,10) : ""}
                       onChange={(e)=>setForm((s)=>({...s, first_contact_at: e.target.value ? new Date(e.target.value).toISOString() : null}))}/>
              </div>
              <div>
                <Label>Notes internes (visibles admin/commercial uniquement)</Label>
                <Textarea rows={6} value={form.internal_notes || ""} onChange={set("internal_notes")}
                          placeholder="Historique des échanges, prochaines actions, RIB, contrat signé le…"
                          data-testid="partner-notes"/>
              </div>
              <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5"/>
                <span>Zone confidentielle : ces notes ne sont jamais visibles par le partenaire lui-même ni sur le site public.</span>
              </div>
            </div>
          )}

          {tab==="history" && (
            <div className="space-y-1 text-sm">
              {data.activity.length === 0 && <div className="text-slate-500 text-center p-6">Aucune action enregistrée.</div>}
              {data.activity.map((a) => (
                <div key={a.id} className="p-2 border-l-2 border-slate-200 pl-3">
                  <div className="font-mono text-xs text-slate-500">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
                  <div><span className="font-semibold">{a.action}</span> par {a.actor_email}</div>
                </div>
              ))}
            </div>
          )}

          <div className="sticky bottom-0 bg-white border-t border-slate-100 pt-3 -mx-4 px-4 flex gap-2">
            <Button disabled={loading} onClick={save} className="btn-shine text-white flex-1" data-testid="partner-save">
              <Save size={14} className="mr-1"/> {loading ? "…" : "Enregistrer"}
            </Button>
            <Button variant="outline" onClick={remove} className="text-rose-600 hover:text-rose-800" data-testid="partner-delete">
              <Trash2 size={14}/>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
